import { AuthContext } from '@repo-level/core-front';
import { useContext, useMemo } from 'react';
import {
  buildLevels,
  getIssuedLevel,
  getLevelThreshold,
  isCleared,
} from '../helpers/levels';
import { resolveStatusGroup } from '../helpers/statusGroup';
import type { EProgressStep, ILevel } from '../types';
import { useIssuedRewards } from './useIssuedRewards';
import { useSettlingReward } from './useSettlingReward';

export interface IUseLevelsResult {
  levels: ILevel[];
  currentLevel: ILevel;
  progress: EProgressStep;
  /** real money bets reached, i.e. the Fortune Points balance */
  reachedAmount: number;
  /** the level whose reward waits to be activated, biggest one first */
  issuedLevel: ILevel | null;
  /** set when that waiting reward is a lootbox the player has to open */
  issuedLootboxId: number | null;
  isPassCompleted: boolean;
  hasStarted: boolean;
  /** level whose reward has been earned but not issued yet, see useSettlingReward */
  settlingOrder: number | null;
}

// the status group is the source of truth, nothing is calculated from bets here
export const useLevels = (): IUseLevelsResult => {
  const { user } = useContext(AuthContext);
  const issuedRewards = useIssuedRewards();

  const result = useMemo(() => {
    const statusGroup = resolveStatusGroup(user);
    const { levels, currentLevel, step } = buildLevels(
      statusGroup,
      issuedRewards
    );
    const issuedLevel = getIssuedLevel(levels);

    return {
      levels,
      currentLevel,
      progress: step,
      reachedAmount: getLevelThreshold(currentLevel, step),
      issuedLevel,
      issuedLootboxId: issuedLevel
        ? (issuedRewards.get(issuedLevel.order) ?? null)
        : null,
      isPassCompleted: levels.every(isCleared),
      hasStarted: statusGroup !== null,
    };
  }, [user, issuedRewards]);

  const settlingOrder = useSettlingReward(result.levels, issuedRewards);

  return { ...result, settlingOrder };
};
